import {
    AirHandlerIcon,
    BoilerIcon,
    ChillerIcon,
    CoolingIcon,
    DryerIcon,
    ElevatorIcon,
    EscalatorIcon,
    HeatingIcon,
    SolarIcon,
    WasherIcon,
    WaterHeaterIcon,
} from "components/atoms/Icon";
import { ReactNode } from "react";
import { PTAC_HEATERS } from "../Survey.constants";
import { equipmentKeyToLabelMap, TerminalUnits } from "./Equipment/BuildingEquipment.constants";
import { EQUIPMENT_SURVEY_FIELD_UNIT_BY_KEY } from "./Equipment/equipmentSurveyFieldUnits";

export interface DataViewStruct {
    title: string;
    items: {
        label: string;
        value: any;
        show: boolean;
        unit?: string;
        icon?: ReactNode;
        hasMissingSizeAndYear?: boolean;
        showNaturalGasWarning?: boolean;
    }[];
}

export const buildRooftopUnitDisplayStruct = (
    rooftopUnit: RooftopUnit,
    showEquipmentWarning?: boolean,
    hasNaturalGasWarning?: boolean
): DataViewStruct => ({
    title: "Packaged rooftop units",
    items: [
        {
            label: "Heating",
            value: equipmentKeyToLabelMap[rooftopUnit.rooftopUnitHeatingType],
            show: rooftopUnit.rooftopUnitHeatingType !== null,
            icon: <HeatingIcon />,
            showNaturalGasWarning:
                hasNaturalGasWarning &&
                showEquipmentWarning &&
                rooftopUnit.rooftopUnitHeatingType?.toLowerCase().includes("gas"),
        },
        {
            label: "Cooling",
            value: equipmentKeyToLabelMap[rooftopUnit.rooftopUnitCoolingType],
            show: rooftopUnit.rooftopUnitCoolingType !== null,
            icon: <CoolingIcon />,
            showNaturalGasWarning:
                hasNaturalGasWarning &&
                showEquipmentWarning &&
                rooftopUnit.rooftopUnitCoolingType?.toLowerCase().includes("gas"),
        },
        {
            label: "Total size",
            value: rooftopUnit.rooftopUnitSupplyAirRate,
            unit: EQUIPMENT_SURVEY_FIELD_UNIT_BY_KEY.rooftopUnitSupplyAirRate,
            show: rooftopUnit.rooftopUnitSupplyAirRate !== null,
            hasMissingSizeAndYear: rooftopUnit.rooftopUnitSupplyAirRate === null,
        },
        {
            label: "Average installation year",
            value: rooftopUnit.rooftopUnitAverageInstallationYear,
            show: rooftopUnit.rooftopUnitAverageInstallationYear !== null,
            hasMissingSizeAndYear: rooftopUnit.rooftopUnitSupplyAirRate === null,
        },
    ],
});

export const buildAirHandlingEquipmentDisplayStruct = (
    airHandlingEquipment: AirHandlingEquipment,
    showEquipmentWarning?: boolean,
    hasNaturalGasWarning?: boolean
): DataViewStruct => ({
    title: "Air handler",
    items: [
        {
            label: "Air Handler",
            value: equipmentKeyToLabelMap[airHandlingEquipment.airHandlingEquipmentType],
            show: airHandlingEquipment.airHandlingEquipmentType !== null,
            icon: <AirHandlerIcon />,
            showNaturalGasWarning:
                hasNaturalGasWarning &&
                showEquipmentWarning &&
                airHandlingEquipment.airHandlingEquipmentType?.toLowerCase().includes("gas"),
        },
        {
            label: "Heating",
            value: equipmentKeyToLabelMap[airHandlingEquipment.airHandlingEquipmentHeatingType],
            show: airHandlingEquipment.airHandlingEquipmentHeatingType !== null,
            showNaturalGasWarning:
                hasNaturalGasWarning &&
                showEquipmentWarning &&
                airHandlingEquipment.airHandlingEquipmentHeatingType?.toLowerCase().includes("gas"),
        },
        {
            label: "Cooling",
            value: equipmentKeyToLabelMap[airHandlingEquipment.airHandlingEquipmentCoolingType],
            show: airHandlingEquipment.airHandlingEquipmentCoolingType !== null,
            showNaturalGasWarning:
                hasNaturalGasWarning &&
                showEquipmentWarning &&
                airHandlingEquipment.airHandlingEquipmentCoolingType?.toLowerCase().includes("gas"),
        },
        {
            label: "Total size",
            value: airHandlingEquipment.airHandlingEquipmentSupplyAirRate,
            unit: EQUIPMENT_SURVEY_FIELD_UNIT_BY_KEY.airHandlingEquipmentSupplyAirRate,
            show: airHandlingEquipment.airHandlingEquipmentSupplyAirRate !== null,
            hasMissingSizeAndYear: airHandlingEquipment.airHandlingEquipmentSupplyAirRate === null,
        },
        {
            label: "Average installation year",
            value: airHandlingEquipment.airHandlingEquipmentAverageInstallationYear,
            show: airHandlingEquipment.airHandlingEquipmentAverageInstallationYear !== null,
            hasMissingSizeAndYear: airHandlingEquipment.airHandlingEquipmentAverageInstallationYear === null,
        },
    ],
});

export const buildCentralPlantDisplayStruct = (
    centralPlantHeater: CentralPlantHeater,
    centralPlantCooler: CentralPlantCooler,
    centralPlantHeatPump: CentralPlantHeatPump,
    genericHVACEquipment: GenericHVACEquipment[],
    showEquipmentWarning?: boolean,
    hasNaturalGasWarning?: boolean
): DataViewStruct => ({
    title: "Central plant",
    items: [
        {
            label: "Boiler",
            value: equipmentKeyToLabelMap[centralPlantHeater.centralPlantHeaterType],
            icon: <BoilerIcon />,
            show: !!centralPlantHeater.centralPlantHeaterExists && centralPlantHeater.centralPlantHeaterType !== null,
            showNaturalGasWarning:
                hasNaturalGasWarning &&
                showEquipmentWarning &&
                centralPlantHeater.centralPlantHeaterType?.toLowerCase().includes("gas"),
        },
        {
            label: "Terminal units",
            value: equipmentKeyToLabelMap[centralPlantHeater.centralPlantHeaterTerminalUnits],
            show:
                !!centralPlantHeater.centralPlantHeaterExists &&
                centralPlantHeater.centralPlantHeaterTerminalUnits !== null,
        },
        {
            label: "Total size",
            value: centralPlantHeater.centralPlantHeaterSize,
            unit: EQUIPMENT_SURVEY_FIELD_UNIT_BY_KEY.centralPlantHeaterSize,
            show: !!centralPlantHeater.centralPlantHeaterExists && centralPlantHeater.centralPlantHeaterSize !== null,
            hasMissingSizeAndYear:
                !!centralPlantHeater.centralPlantHeaterExists && centralPlantHeater.centralPlantHeaterSize === null,
        },
        {
            label: "Average installation year",
            value: centralPlantHeater.centralPlantHeaterAverageInstallationYear,
            show:
                !!centralPlantHeater.centralPlantHeaterExists &&
                centralPlantHeater.centralPlantHeaterAverageInstallationYear !== null,
            hasMissingSizeAndYear:
                !!centralPlantHeater.centralPlantHeaterExists &&
                centralPlantHeater.centralPlantHeaterAverageInstallationYear === null,
        },

        {
            label: "Central heat pump",
            value: equipmentKeyToLabelMap[centralPlantHeatPump.centralPlantHeatPumpType],
            icon: <BoilerIcon />,
            show:
                !!centralPlantHeatPump.centralPlantHeatPumpExists &&
                centralPlantHeatPump.centralPlantHeatPumpType !== null,
        },
        {
            label: "Total size",
            value: centralPlantHeatPump.centralPlantHeatPumpSize,
            unit: EQUIPMENT_SURVEY_FIELD_UNIT_BY_KEY.centralPlantHeatPumpSize,
            show:
                !!centralPlantHeatPump.centralPlantHeatPumpExists &&
                centralPlantHeatPump.centralPlantHeatPumpType !== null,
            hasMissingSizeAndYear:
                !!centralPlantHeatPump.centralPlantHeatPumpExists &&
                centralPlantHeatPump.centralPlantHeatPumpSize === null,
        },
        {
            label: "Chiller",
            value: equipmentKeyToLabelMap[centralPlantCooler.centralPlantCoolerType],
            icon: <ChillerIcon />,
            show: !!centralPlantCooler.centralPlantCoolerExists && centralPlantCooler.centralPlantCoolerType !== null,
            showNaturalGasWarning:
                hasNaturalGasWarning &&
                showEquipmentWarning &&
                centralPlantCooler.centralPlantCoolerType?.toLowerCase().includes("gas"),
        },
        {
            label: "Terminal units",
            value: equipmentKeyToLabelMap[centralPlantCooler.centralPlantCoolerTerminalUnits],
            show:
                !!centralPlantCooler.centralPlantCoolerExists &&
                centralPlantCooler.centralPlantCoolerTerminalUnits !== null,
            showNaturalGasWarning:
                hasNaturalGasWarning &&
                showEquipmentWarning &&
                centralPlantCooler.centralPlantCoolerTerminalUnits?.toLowerCase().includes("gas"),
        },
        {
            label: "Total size",
            value: centralPlantCooler.centralPlantCoolerSize,
            unit: EQUIPMENT_SURVEY_FIELD_UNIT_BY_KEY.centralPlantCoolerSize,
            show: !!centralPlantCooler.centralPlantCoolerExists && centralPlantCooler.centralPlantCoolerSize !== null,
            hasMissingSizeAndYear:
                !!centralPlantCooler.centralPlantCoolerExists && centralPlantCooler.centralPlantCoolerSize === null,
        },
        {
            label: "Average installation year",
            value: centralPlantCooler.centralPlantCoolerAverageInstallationYear,
            show:
                !!centralPlantCooler.centralPlantCoolerExists &&
                centralPlantCooler.centralPlantCoolerAverageInstallationYear !== null,
            hasMissingSizeAndYear:
                !!centralPlantCooler.centralPlantCoolerExists &&
                centralPlantCooler.centralPlantCoolerAverageInstallationYear === null,
        },
        { label: "separator", show: true, value: "" },

        ...genericHVACEquipment.map((equipment) => ({
            label: "Custom Equipment",
            value: `${equipment.name}`,
            show: true,
        })),
        ...genericHVACEquipment.map((equipment) => ({
            label: "Size",
            value: `${equipment.size} ${equipment.sizeUnits}`,
            show: true,
        })),

        ...genericHVACEquipment.map((equipment) => ({
            label: "Fuel",
            value: `${equipmentKeyToLabelMap[equipment.fuel]}`,
            show: true,
        })),
        ...genericHVACEquipment.map((equipment) => ({
            label: "Installation Year",
            value: `${equipment.installationYear}`,
            show: true,
        })),
        ...genericHVACEquipment.map((equipment) => ({
            label: "Life Span",
            value: `${equipment.lifeSpan} years`,
            show: true,
        })),
        ...genericHVACEquipment.map((equipment) => ({
            label: "Load Ratio",
            value: `${equipment.loadRatio}`,
            show: true,
        })),
        ...genericHVACEquipment.map((equipment) => ({
            label: "End use",
            value: `${equipmentKeyToLabelMap[equipment.endUse]}`,
            show: true,
        })),
    ],
});

export const buildTerminalUnitsDisplayStruct = (
    terminalUnits: TerminalUnits,
    heatPump: HeatPump,
    showEquipmentWarning?: boolean,
    hasNaturalGasWarning?: boolean
): DataViewStruct => ({
    title: "Terminal units",
    items: [
        {
            label: "Cooling terminal units",
            value: equipmentKeyToLabelMap[terminalUnits.terminalCoolerUnits],
            show: !!terminalUnits.terminalCoolerExists && terminalUnits.terminalCoolerUnits !== null,
            icon: <ChillerIcon />,
            showNaturalGasWarning:
                hasNaturalGasWarning &&
                showEquipmentWarning &&
                terminalUnits.terminalCoolerUnits?.toLowerCase().includes("gas"),
        },
        {
            label: "Total size",
            unit: EQUIPMENT_SURVEY_FIELD_UNIT_BY_KEY.terminalCoolerSize,
            value: terminalUnits.terminalCoolerSize,
            show: !!terminalUnits.terminalCoolerExists && terminalUnits.terminalCoolerSize !== null,
            hasMissingSizeAndYear: !!terminalUnits.terminalCoolerExists && terminalUnits.terminalCoolerSize === null,
        },
        {
            label: "Average installation year",
            value: terminalUnits.terminalCoolerAverageInstallationYear,
            show: !!terminalUnits.terminalCoolerExists && terminalUnits.terminalCoolerAverageInstallationYear !== null,
            hasMissingSizeAndYear: !!terminalUnits.terminalCoolerExists && terminalUnits.terminalCoolerSize === null,
        },
        {
            label: "Heating terminal units",
            value: equipmentKeyToLabelMap[terminalUnits.terminalHeaterUnits],
            show: !!terminalUnits.terminalHeaterExists && terminalUnits.terminalHeaterUnits !== null,
            icon: <BoilerIcon />,
            showNaturalGasWarning:
                hasNaturalGasWarning &&
                showEquipmentWarning &&
                terminalUnits.terminalHeaterUnits?.toLowerCase().includes("gas"),
        },
        {
            label: "Total size",
            unit: EQUIPMENT_SURVEY_FIELD_UNIT_BY_KEY.terminalHeaterSize,
            value: terminalUnits.terminalHeaterSize,
            show:
                !!terminalUnits.terminalHeaterExists &&
                terminalUnits.terminalHeaterSize !== null &&
                !PTAC_HEATERS.includes(terminalUnits.terminalHeaterUnits),
            hasMissingSizeAndYear: !!terminalUnits.terminalHeaterExists && terminalUnits.terminalHeaterSize === null,
        },
        {
            label: "Total heater size",
            unit: EQUIPMENT_SURVEY_FIELD_UNIT_BY_KEY.terminalHeaterSize,
            value: terminalUnits.terminalHeaterSize,
            show:
                !!terminalUnits.terminalHeaterExists &&
                terminalUnits.terminalHeaterSize !== null &&
                PTAC_HEATERS.includes(terminalUnits.terminalHeaterUnits),
            hasMissingSizeAndYear: !!terminalUnits.terminalHeaterExists && terminalUnits.terminalHeaterSize === null,
        },
        {
            label: "Total cooler size",
            unit: EQUIPMENT_SURVEY_FIELD_UNIT_BY_KEY.terminalHeaterCoolerSize,
            value: terminalUnits.terminalHeaterCoolerSize,
            show:
                !!terminalUnits.terminalHeaterExists &&
                terminalUnits.terminalHeaterSize !== null &&
                PTAC_HEATERS.includes(terminalUnits.terminalHeaterUnits),
            hasMissingSizeAndYear: !!terminalUnits.terminalHeaterExists && terminalUnits.terminalHeaterSize === null,
        },
        {
            label: "Average installation year",
            value: terminalUnits.terminalHeaterAverageInstallationYear,
            show: !!terminalUnits.terminalHeaterExists && terminalUnits.terminalHeaterAverageInstallationYear !== null,
            hasMissingSizeAndYear:
                !!terminalUnits.terminalHeaterExists && terminalUnits.terminalHeaterAverageInstallationYear === null,
        },
        {
            label: "Heat pump terminal unit",
            value: equipmentKeyToLabelMap[heatPump.heatPumpType!],
            show: !!heatPump.heatPumpExists && heatPump.heatPumpType !== null,
            icon: <BoilerIcon />,
        },
        {
            label: "Total size",
            unit: EQUIPMENT_SURVEY_FIELD_UNIT_BY_KEY.heatPumpSize,
            value: heatPump.heatPumpSize,
            show: !!heatPump.heatPumpExists && heatPump.heatPumpSize !== null,
            hasMissingSizeAndYear: !!heatPump.heatPumpExists && heatPump.heatPumpSize === null,
        },
        {
            label: "Average installation year",
            value: heatPump.heatPumpInstallationYear,
            show: !!heatPump.heatPumpExists && heatPump.heatPumpInstallationYear !== null,
            hasMissingSizeAndYear: !!heatPump.heatPumpExists && heatPump.heatPumpInstallationYear === null,
        },
    ],
});

export const buildDomesticHotWaterDisplayStruct = (
    domesticHotWaterHeater: DomesticHotWaterHeater,
    showEquipmentWarning?: boolean,
    hasNaturalGasWarning?: boolean
): DataViewStruct => ({
    title: "Domestic water heater",
    items: [
        {
            label: "Water heater",
            value: equipmentKeyToLabelMap[domesticHotWaterHeater.domesticHotWaterHeaterType],
            icon: <WaterHeaterIcon />,
            show:
                !!domesticHotWaterHeater.domesticHotWaterHeaterExists &&
                domesticHotWaterHeater.domesticHotWaterHeaterType !== null,
            showNaturalGasWarning:
                hasNaturalGasWarning &&
                showEquipmentWarning &&
                domesticHotWaterHeater.domesticHotWaterHeaterType?.toLowerCase().includes("gas"),
        },
        {
            label: "Total size",
            unit: EQUIPMENT_SURVEY_FIELD_UNIT_BY_KEY.domesticHotWaterHeaterSize,
            value: domesticHotWaterHeater.domesticHotWaterHeaterSize,
            show:
                !!domesticHotWaterHeater.domesticHotWaterHeaterExists &&
                domesticHotWaterHeater.domesticHotWaterHeaterSize !== null,
            hasMissingSizeAndYear:
                !!domesticHotWaterHeater.domesticHotWaterHeaterExists &&
                domesticHotWaterHeater.domesticHotWaterHeaterSize === null,
        },
        {
            label: "Average installation year",
            value: domesticHotWaterHeater.domesticHotWaterHeaterAverageInstallationYear,
            show:
                !!domesticHotWaterHeater.domesticHotWaterHeaterExists &&
                domesticHotWaterHeater.domesticHotWaterHeaterAverageInstallationYear !== null,
            hasMissingSizeAndYear:
                !!domesticHotWaterHeater.domesticHotWaterHeaterExists &&
                domesticHotWaterHeater.domesticHotWaterHeaterAverageInstallationYear === null,
        },
        {
            label: "Central Distribution",
            value: domesticHotWaterHeater.domesticHotWaterHeaterCentralDistribution ? "Yes" : "No",
            show:
                !!domesticHotWaterHeater.domesticHotWaterHeaterExists &&
                domesticHotWaterHeater.domesticHotWaterHeaterCentralDistribution !== null,
            hasMissingSizeAndYear:
                !!domesticHotWaterHeater.domesticHotWaterHeaterExists &&
                domesticHotWaterHeater.domesticHotWaterHeaterAverageInstallationYear === null,
        },
    ],
});

export const buildOtherEquipmentDisplayStruct = (
    otherEquipment: OtherEquipment,
    showEquipmentWarning?: boolean,
    hasNaturalGasWarning?: boolean
): DataViewStruct => ({
    title: "Additional equipment",
    items: [
        {
            label: "Does the building have clothes dryers?",
            value: equipmentKeyToLabelMap[otherEquipment.clothesDryersType],
            icon: <DryerIcon />,
            show: !!otherEquipment.clothesDryersExists && otherEquipment.clothesDryersType !== null,
            showNaturalGasWarning:
                hasNaturalGasWarning &&
                showEquipmentWarning &&
                otherEquipment.clothesDryersType?.toLowerCase().includes("gas"),
        },
        {
            label: "What is their energy density?",
            unit: EQUIPMENT_SURVEY_FIELD_UNIT_BY_KEY.clothesDryersEnergyDensity,
            value: otherEquipment.clothesDryersEnergyDensity,
            show: !!otherEquipment.clothesDryersExists && otherEquipment.clothesDryersEnergyDensity !== null,
            hasMissingSizeAndYear:
                !!otherEquipment.clothesDryersExists && otherEquipment.clothesDryersEnergyDensity === null,
        },
        {
            label: "When were they installed (avg)?",
            value: otherEquipment.clothesDryersAverageInstallationYear,
            show: !!otherEquipment.clothesDryersExists && otherEquipment.clothesDryersAverageInstallationYear !== null,
            hasMissingSizeAndYear:
                !!otherEquipment.clothesDryersExists && otherEquipment.clothesDryersAverageInstallationYear === null,
        },
        {
            label: "Does the building have clothes washers?",
            value: "Yes",
            icon: <WasherIcon />,
            show: !!otherEquipment.clothesWashersExists,
        },
        {
            label: "What is their energy density?",
            unit: EQUIPMENT_SURVEY_FIELD_UNIT_BY_KEY.clothesWashersEnergyDensity,
            value: otherEquipment.clothesWashersEnergyDensity,
            show: !!otherEquipment.clothesWashersExists && otherEquipment.clothesWashersEnergyDensity !== null,
            hasMissingSizeAndYear:
                !!otherEquipment.clothesWashersExists && otherEquipment.clothesWashersEnergyDensity === null,
        },
        {
            label: "When were they installed (avg)?",
            value: otherEquipment.clothesWashersAverageInstallationYear,
            show:
                !!otherEquipment.clothesWashersExists && otherEquipment.clothesWashersAverageInstallationYear !== null,
            hasMissingSizeAndYear:
                !!otherEquipment.clothesWashersExists && otherEquipment.clothesWashersAverageInstallationYear === null,
        },
        {
            label: "Does the building have elevators?",
            value: "Yes",
            icon: <ElevatorIcon />,
            show: !!otherEquipment.elevatorsExists,
        },
        {
            label: "How many elevators?",
            value: otherEquipment.elevatorsQuantity,
            show: !!otherEquipment.elevatorsExists && otherEquipment.elevatorsQuantity !== null,
            hasMissingSizeAndYear: !!otherEquipment.elevatorsExists && otherEquipment.elevatorsQuantity === null,
        },
        {
            label: "When were they installed (avg)?",
            value: otherEquipment.elevatorsAverageInstallationYear,
            show: !!otherEquipment.elevatorsExists && otherEquipment.elevatorsAverageInstallationYear !== null,
            hasMissingSizeAndYear:
                !!otherEquipment.elevatorsExists && otherEquipment.elevatorsAverageInstallationYear === null,
        },
        {
            label: "Does the building have escalators?",
            value: "Yes",
            icon: <EscalatorIcon />,
            show: !!otherEquipment.escalatorExists,
        },
        {
            label: "How many?",
            value: otherEquipment.escalatorQuantity,
            show: !!otherEquipment.escalatorExists && otherEquipment.escalatorQuantity !== null,
            hasMissingSizeAndYear: !!otherEquipment.escalatorExists && otherEquipment.escalatorQuantity === null,
        },
        {
            label: "When were they installed (avg)?",
            value: otherEquipment.escalatorInstallationYear,
            show: !!otherEquipment.escalatorExists && otherEquipment.escalatorInstallationYear !== null,
            hasMissingSizeAndYear:
                !!otherEquipment.escalatorExists && otherEquipment.escalatorInstallationYear === null,
        },
        {
            label: "Does the building have rooftop photovoltaics?",
            value: "Yes",
            icon: <SolarIcon />,
            show: !!otherEquipment.rooftopPhotovoltaicsExists,
        },
        {
            label: "What is their total capacity?",
            value: otherEquipment.rooftopPhotovoltaicsSize,
            unit: EQUIPMENT_SURVEY_FIELD_UNIT_BY_KEY.rooftopPhotovoltaicsSize,
            show: !!otherEquipment.rooftopPhotovoltaicsExists && otherEquipment.rooftopPhotovoltaicsSize !== null,
            hasMissingSizeAndYear:
                !!otherEquipment.rooftopPhotovoltaicsExists && otherEquipment.rooftopPhotovoltaicsSize === null,
        },
        {
            label: "When were they installed (avg)?",
            value: otherEquipment.rooftopPhotovoltaicsAverageInstallationYear,
            show:
                !!otherEquipment.rooftopPhotovoltaicsExists &&
                otherEquipment.rooftopPhotovoltaicsAverageInstallationYear !== null,
            hasMissingSizeAndYear:
                !!otherEquipment.rooftopPhotovoltaicsExists &&
                otherEquipment.rooftopPhotovoltaicsAverageInstallationYear === null,
        },
    ],
});
